/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './LookupSelect';
import { isEmpty } from 'utils/isEmpty';

const LookupSelectManager: ComponentManagerFn<'LookupSelect'> = (
  platform: Platform,
) => {
  let callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'LookupSelect'>(platform, 'LookupSelect'),
    type: 'LookupSelect',
    name: 'Lookup Select',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnChange',
          'TriggerOnClick',
          'LookupReturnValueMapping',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      lookup: {
        label: 'Lookup',
        type: 'LookupCodeChooser',
      },
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: (item: RequiredItem<'LookupSelect'>) => {
          return !isEmpty(item.props.lookup.code)
            ? item.props.lookup.isNumeric
              ? ['Integer', 'Decimal', 'Double']
              : ['String', 'YN', 'TF']
            : ['String', 'YN', 'TF', 'Integer', 'Decimal', 'Double'];
        },
        attributeRefreshCallback: (cb: () => void) => {
          callback = cb;
        },
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      unknownValueMessage: {
        label: 'Unknown Value Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Unknown Value Error Message',
        category: 'Data',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'lookup') {
        callback();
      }
      return {
        [property]: value,
      };
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Forms',
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    replaceableWith: ['NumberField', 'Select', 'TextField'],
    description: `**Overview**<br/>
The Lookup Select component is a standardized dropdown interface designed to display data from centralized **Lookup Tables**. Unlike a standard **Select** component (where options are often hardcoded) or an **LOV Field** (which queries dynamic transaction data), the Lookup Select is optimized for system-wide reference lists—such as "Country Codes," "Currency Types," or "Order Statuses."
Use this component when you need users to pick from a managed list of values that is defined once in the system and reused across multiple forms.

**1. Data Source (Lookups)**<br/>
The core function of this component is driven by the **Lookup** property.
* **Lookup Code:** You select a specific "Lookup Code" definition from the system (e.g., "STD_COUNTRIES" or "HR_DEPARTMENTS").
* **Central Management:** The options displayed in the dropdown are controlled by the system's central lookup configuration, meaning if an admin adds a new option to the central list, it automatically appears in all **Lookup Select** components using that code.

**2. Smart Data Binding**<br/>
The component includes intelligent logic to ensure the data type of your form field matches the data type of the selected Lookup.
* **View Attribute:** The Data Store field where the selection is saved.
* **Type Enforcement:**
    * **Numeric Lookups:** If the underlying Lookup is defined as numeric (e.g., HTTP Status Codes like 200, 404), the component allows binding only to **Integer**, **Decimal**, or **Double** attributes.
    * **String Lookups:** If the Lookup is text-based (e.g., "Male/Female"), it supports **String**, **YN** (Yes/No), and **TF** (True/False) attributes.

**3. Validation & Handling**<br/>
The component ensures data integrity by verifying that selected values actually exist in the allowed list.
* **Server Validate On Change:** A boolean toggle. If enabled, the system validates the selection against the server immediately upon change.
* **Unknown Value Message:** An expression allowing you to define a custom error message (e.g., "This code is no longer active") if the field contains a value that is not found in the current Lookup list.

**4. Advanced Mapping**<br/>
The Lookup Select supports the **LookupReturnValueMapping** child component.
* **Function:** This allows you to map additional attributes associated with the lookup value. 
* **Example:** When a user selects a currency code "USD", you can map the lookup's description "US Dollar" to a secondary read-only field on your form.

**5. Best Use Cases**<br/>
* **Status Indicators:** Selecting "Draft," "Pending," or "Approved" from a workflow status list.
* **Standard Classifications:** Picking "Priority Levels" (High/Medium/Low) for support tickets.
* **Global Standards:** Selecting ISO codes for Countries, Languages, or Currencies.
* **Configuration Flags:** Setting simple boolean flags like "Yes/No" or "True/False" using a friendly dropdown interface.`,
  };
};

export default LookupSelectManager;
